import {
  Currency,
  ErrorResponse,
  PayoutAccountOrCard,
  PayoutSetting,
  PayoutsApi,
  PayoutSettingList,
  TaxRate,
} from '@citruscamps/citrus-client'
import { useQuery } from 'react-query'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'
import { sendEvent } from '../../../../../lib/gtag'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey } from '../../../../../utils/key-generator'
import { toQueryParams } from '../../../../../utils/query-params'

export interface TaxRateTotal extends TaxRate {
  amount: Currency
}

interface IProps {
  programId?: string
}

interface FetchPayoutSettings {
  active?: PayoutSetting
  data: PayoutSetting[]
  externalAccounts: Array<PayoutAccountOrCard>
  error: ErrorResponse | null
  isLoading: boolean
  isError: boolean
  handleSetupAccount: () => void
  handleViewAccount: () => void
  handleViewLegacyAccount: (account_id?: string) => void
}

export const useFetchPayoutSettings = ({ programId }: IProps): FetchPayoutSettings => {
  const { requestHandler } = useRequestHandler()
  const queryKeys = generateBaseKey({
    type: 'payout',
    program_id: programId,
  })
  const {
    data: resp,
    error,
    isError,
    isLoading,
  } = useQuery<PayoutSettingList, ErrorResponse>(
    queryKeys,
    async ({ signal }) => {
      if (!programId) {
        throw new Error('Unable to fetch data')
      }
      const client = new PayoutsApi(generateApiConfig())
      return await requestHandler<PayoutSettingList>(() =>
        client.findSettingsPayoutSetting({ program_id: programId }, { signal }),
      )
    },
    { enabled: !!programId },
  )
  const active: PayoutSetting | undefined = (resp?.data || []).find(
    (p: PayoutSetting) => p.is_active,
  )

  const handleSetupAccount = (): void => {
    const authorizeUri = `${process.env.REACT_APP_API_BASE_URL}/api/v1/programs/${programId}/payouts/onboard`
    sendEvent({
      label: 'Setup Payouts',
      category: 'Lead',
      step: 'program_payouts',
      action: 'setup',
    })
    window.open(authorizeUri, '_self')
  }

  const handleViewAccount = (): void => {
    const dashboardUri = `${process.env.REACT_APP_API_BASE_URL}/api/v1/programs/${programId}/payouts/dashboard_standard`
    window.open(dashboardUri, '_self')
  }

  const handleViewLegacyAccount = (account_id?: string): void => {
    const query: string = toQueryParams({ account_id })
    const dashboardUri = `${process.env.REACT_APP_API_BASE_URL}/api/v1/programs/${programId}/payouts/dashboard_express?${query}`
    window.open(dashboardUri, '_blank')
  }

  return {
    active,
    data: resp?.data || [],
    error,
    externalAccounts: active?.external_accounts?.data || [],
    isError,
    isLoading,
    handleSetupAccount,
    handleViewAccount,
    handleViewLegacyAccount,
  }
}
